// Frontend entry point, bundled into backend/static/bundle.js by esbuild.

interface ClientConfig {
  ticketUrl: string;
  loginUrl: string;
  clientId: string;
  devOverride: boolean;
  devUserId: number;
}

interface Answer {
  q: string;
  verdict: string;
  answer: string;
  comment: string | null;
  // Asked by the host via /подсказка.
  hint?: boolean;
}

interface Solution {
  text: string;
  number: number | null;
  author: string;
  url: string;
}

interface AskResponse extends Answer {
  solved: boolean;
  solution?: Solution;
  hints_left?: number | null; // null: unlimited (admins)
}

interface SolvedSummary {
  token: number;
  title: string;
  solved_at: string;
  questions: number;
}

interface SolvedRiddle {
  title: string;
  question: string;
  solved_at: string;
  history: Answer[];
  solution: Solution;
}

interface State {
  user_id: number;
  solved_count: number;
  total: number;
  is_admin: boolean;
  riddle: {
    token: number;
    title: string;
    question: string;
  } | null;
  history?: Answer[];
  solved?: boolean;
  solution?: Solution;
}

declare global {
  interface Window {
    DANETKA: ClientConfig;
  }
}

const config = window.DANETKA;
const params = new URLSearchParams(location.search);
const TICKET_REFRESH_MS = 30_000;
const SSO_TIMEOUT_MS = 10_000;

const header = document.getElementById("header")!;
const transcript = document.getElementById("transcript")!;
const form = document.getElementById("prompt") as HTMLFormElement;
const input = document.getElementById("input") as HTMLTextAreaElement;

// Grow the textarea to fit its content so long questions wrap visibly.
function fitInput(): void {
  input.style.height = "auto";
  input.style.height = `${input.scrollHeight}px`;
}

function setInput(value: string): void {
  input.value = value;
  fitInput();
}

type Child = Node | string | null | undefined | false;

function el(
  tag: string,
  attrs: Record<string, string> = {},
  ...children: Child[]
): HTMLElement {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const child of children) {
    if (child) node.append(child);
  }
  return node;
}

function scrollDown(): void {
  window.scrollTo(0, document.body.scrollHeight);
}

// ---- Auth ----

class NotLoggedIn extends Error {}

let ticket: string | null = null;

function devUserId(): number {
  const as = Number(params.get("as"));
  return Number.isInteger(as) && as > 0 ? as : config.devUserId;
}

async function fetchTicket(): Promise<string> {
  const url = `${config.ticketUrl}?client_id=${encodeURIComponent(config.clientId)}`;
  let response: Response;
  try {
    response = await fetch(url, {
      credentials: "include",
      signal: AbortSignal.timeout(SSO_TIMEOUT_MS),
    });
  } catch (e) {
    throw new Error(
      `Не удалось связаться с ${config.loginUrl} (${e instanceof Error ? e.message : String(e)}).`,
    );
  }
  if (response.status === 401 || response.status === 403) {
    throw new NotLoggedIn();
  }
  if (!response.ok) throw new Error(`SSO error ${response.status}`);
  const data = (await response.json()) as { ticket: string };
  return data.ticket;
}

async function initAuth(): Promise<void> {
  if (config.devOverride) return;
  ticket = await fetchTicket();
  setInterval(() => {
    fetchTicket().then(
      (t) => {
        ticket = t;
      },
      (e: unknown) => {
        if (e instanceof NotLoggedIn) ticket = null;
        console.warn("Ticket refresh failed", e);
      },
    );
  }, TICKET_REFRESH_MS);
}

function authHeaders(): Record<string, string> {
  if (config.devOverride) return { "X-Dev-User": String(devUserId()) };
  if (!ticket) throw new NotLoggedIn();
  return { Authorization: `Bearer ${ticket}` };
}

class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message);
  }
}

// Identifies the riddle this page shows; the server rejects questions for a
// riddle that was meanwhile solved and replaced in another tab.
let riddleToken: number | null = null;

async function api<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = authHeaders();
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const response = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  };
  if (response.status === 401) throw new NotLoggedIn();
  if (!response.ok) {
    throw new ApiError(data.error ?? `Ошибка ${response.status}`, data.code);
  }
  return data as T;
}

function showLogin(): void {
  form.hidden = true;
  header.replaceChildren(el("h1", {}, "Данетки"));
  transcript.replaceChildren(
    el(
      "p",
      { class: "system" },
      "Чтобы играть, войдите на ",
      el("a", { href: config.loginUrl, target: "_blank" }, "db.crem.xyz"),
      " и обновите страницу.",
    ),
  );
}

// ---- Game ----

const HINT_COMMAND = "/подсказка";

function renderAnswer(a: Answer): HTMLElement {
  if (a.hint) {
    return el(
      "div",
      { class: "entry hint" },
      el("div", { class: "q" }, `> ${HINT_COMMAND}`),
      el("div", { class: "hint-q" }, `Подсказка: ${a.q}`),
      el("div", { class: `a ${a.verdict}` }, a.answer),
      a.comment && el("div", { class: "comment" }, a.comment),
    );
  }
  return el(
    "div",
    { class: "entry" },
    el("div", { class: "q" }, `> ${a.q}`),
    el("div", { class: `a ${a.verdict}` }, a.answer),
    a.comment && el("div", { class: "comment" }, a.comment),
  );
}

function renderSolution(solution: Solution, withNext = true): HTMLElement {
  const next = el("button", { type: "button" }, "Следующая данетка →");
  next.addEventListener("click", () => {
    next.setAttribute("disabled", "");
    // Focus the prompt while the tap still counts as a user gesture, so the
    // mobile keyboard opens; loadGame() re-hides the form if needed.
    form.hidden = false;
    busy = true;
    input.focus();
    api("POST", "/api/next")
      .then(loadGame)
      .catch(handleError)
      .finally(() => {
        busy = false;
      });
  });
  return el(
    "div",
    { class: "solution" },
    el("h2", {}, "Разгадка"),
    solution.text,
    el(
      "p",
      {},
      solution.url
        ? el(
            "a",
            { href: solution.url, target: "_blank", rel: "noopener" },
            `Данетка №${solution.number ?? "?"} на danetka.ru`,
          )
        : `Данетка №${solution.number ?? "?"}`,
      solution.author && `. Автор: ${solution.author}`,
    ),
    withNext && el("div", {}, el("br"), next),
  );
}

// Keeps the dev user across in-app links.
function pageHref(query: string): string {
  const as = config.devOverride ? `as=${devUserId()}` : "";
  const q = [query, as].filter(Boolean).join("&");
  return q ? `?${q}` : "/";
}

function pageLink(query: string, text: string): HTMLElement {
  return el("a", { href: pageHref(query) }, text);
}

function renderHeader(state: State): void {
  const r = state.riddle;
  const meta = el(
    "div",
    { class: "meta" },
    el(
      "span",
      {},
      config.devOverride && `[DEV: user ${state.user_id}]`,
      " ",
      state.is_admin && pageLink("admin", "админка"),
      " ",
      state.solved_count > 0
        ? pageLink("solved", `Решено: ${state.solved_count}`)
        : `Решено: ${state.solved_count}`,
      ` / ${state.total}`,
    ),
  );
  header.replaceChildren(
    el("h1", {}, r ? r.title : "Данетки"),
    el("p", { class: "question" }, r?.question),
    meta,
  );
}

async function loadGame(): Promise<void> {
  const state = await api<State>("GET", "/api/state");
  riddleToken = state.riddle?.token ?? null;
  renderHeader(state);
  transcript.replaceChildren();
  if (!state.riddle) {
    form.hidden = true;
    transcript.append(
      el("p", { class: "system" }, "Поздравляем, вы разгадали все данетки!"),
    );
    return;
  }
  for (const a of state.history ?? []) transcript.append(renderAnswer(a));
  if (state.solved && state.solution) {
    form.hidden = true;
    transcript.append(renderSolution(state.solution));
  } else {
    form.hidden = false;
    input.focus();
  }
  scrollDown();
}

// ---- Solved riddles ----

function solvedNav(...links: HTMLElement[]): HTMLElement {
  const parts: Child[] = [];
  for (const link of links) parts.push(parts.length ? " · " : "", link);
  return el("div", { class: "meta" }, ...parts);
}

async function renderSolvedList(): Promise<void> {
  const riddles = await api<SolvedSummary[]>("GET", "/api/solved");
  header.replaceChildren(
    el("h1", {}, "Решённые данетки"),
    solvedNav(pageLink("", "← к игре")),
  );
  transcript.replaceChildren(
    riddles.length
      ? el(
          "ol",
          { class: "solved-list" },
          ...riddles.map((r) =>
            el(
              "li",
              {},
              pageLink(`solved=${r.token}`, r.title),
              ` — ${r.questions} вопр., ${fmtDateTime(r.solved_at)}`,
            ),
          ),
        )
      : el("p", { class: "system" }, "Решённых данеток пока нет."),
  );
}

async function renderSolvedRiddle(token: string): Promise<void> {
  const r = await api<SolvedRiddle>("GET", `/api/solved/${token}`);
  header.replaceChildren(
    el("h1", {}, r.title),
    el("p", { class: "question" }, r.question),
    solvedNav(pageLink("solved", "← все решённые"), pageLink("", "к игре")),
  );
  transcript.replaceChildren(
    ...r.history.map(renderAnswer),
    renderSolution(r.solution, false),
  );
}

async function loadSolved(): Promise<void> {
  document.getElementById("intro")!.hidden = true;
  form.hidden = true;
  const token = params.get("solved");
  await (token ? renderSolvedRiddle(token) : renderSolvedList());
}

function handleError(e: unknown): void {
  if (e instanceof NotLoggedIn) {
    showLogin();
    return;
  }
  const message = e instanceof Error ? e.message : String(e);
  transcript.append(el("p", { class: "system error" }, message));
  scrollDown();
}

const inputHistory: string[] = [];
let historyPos = 0;
// A request is pending. The input stays enabled meanwhile: disabling it would
// drop focus and close the mobile keyboard.
let busy = false;

// Sends a question (or a command, shown as typed) and renders the answer.
async function submit(
  text: string,
  path: string,
  body: Record<string, unknown>,
): Promise<void> {
  inputHistory.push(text);
  historyPos = inputHistory.length;
  setInput("");
  busy = true;
  input.placeholder = "…";
  const pending = el(
    "div",
    { class: "entry" },
    el("div", { class: "q" }, `> ${text}`),
    el("div", { class: "system" }, "…"),
  );
  transcript.append(pending);
  scrollDown();
  try {
    const result = await api<AskResponse>("POST", path, {
      ...body,
      riddle_token: riddleToken,
    });
    pending.replaceWith(renderAnswer(result));
    if (result.solved && result.solution) {
      form.hidden = true;
      transcript.append(renderSolution(result.solution));
    }
  } catch (e) {
    pending.remove();
    if (e instanceof ApiError && e.code === "riddle_changed") {
      // The question was about the old riddle; show the current one instead.
      await loadGame().catch(handleError);
    } else if (!input.value) {
      // Don't clobber anything typed while waiting.
      setInput(text);
    }
    handleError(e);
  } finally {
    busy = false;
    input.placeholder = "";
    if (!form.hidden) input.focus();
    scrollDown();
  }
}

function runCommand(text: string): void {
  const command = text.toLowerCase();
  if (command === HINT_COMMAND || command === "/hint") {
    void submit(text, "/api/hint", {});
    return;
  }
  inputHistory.push(text);
  historyPos = inputHistory.length;
  setInput("");
  transcript.append(
    el(
      "div",
      { class: "entry" },
      el("div", { class: "q" }, `> ${text}`),
      el(
        "div",
        { class: "system error" },
        `Неизвестная команда. Доступна: ${HINT_COMMAND}`,
      ),
    ),
  );
  scrollDown();
}

form.addEventListener("submit", (e) => {
  e.preventDefault();
  const text = input.value.trim();
  if (!text || busy) return;
  if (text.startsWith("/")) runCommand(text);
  else void submit(text, "/api/ask", { text });
});

input.addEventListener("input", () => {
  const before = input.style.height;
  fitInput();
  if (input.style.height !== before) scrollDown();
});

// Console-like: a click anywhere puts the caret in the prompt, unless it hit
// something interactive or the user was selecting text to copy.
document.addEventListener("click", (e) => {
  if (form.hidden || busy) return;
  if (document.body.classList.contains("admin")) return;
  const target = e.target as Element | null;
  if (target?.closest("a, button, input, textarea, select, summary, label")) {
    return;
  }
  if (!window.getSelection()?.isCollapsed) return;
  input.focus({ preventScroll: true });
});

input.addEventListener("keydown", (e) => {
  if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
    e.preventDefault();
    form.requestSubmit();
    return;
  }
  // When the text wraps, let the arrows move the caret between lines; only
  // recall history from the very start (up) or end (down) of the text.
  const lineHeight = parseFloat(getComputedStyle(input).lineHeight) || 0;
  const multiline = input.scrollHeight > lineHeight * 1.5;
  const atStart = input.selectionStart === 0 && input.selectionEnd === 0;
  const atEnd = input.selectionStart === input.value.length;
  if (e.key === "ArrowUp" && historyPos > 0 && (!multiline || atStart)) {
    historyPos -= 1;
  } else if (
    e.key === "ArrowDown" &&
    historyPos < inputHistory.length &&
    (!multiline || atEnd)
  ) {
    historyPos += 1;
  } else {
    return;
  }
  e.preventDefault();
  setInput(inputHistory[historyPos] ?? "");
});

// ---- Admin ----

interface LlmStats {
  calls: number;
  failed_calls: number;
  prompt_tokens: number;
  cached_tokens: number;
  completion_tokens: number;
  cost: number;
}

interface AdminUser extends LlmStats {
  user_id: number;
  solved: number;
  started: number;
  questions: number;
  last_activity: string;
  current_title: string | null;
}

interface AdminMonth extends LlmStats {
  month: string;
  users: number;
}

interface AdminOverview {
  users: AdminUser[];
  months: AdminMonth[];
}

interface AdminQuestion {
  id: number;
  created_at: string;
  text: string;
  verdict: string;
  comment: string | null;
  justification: string;
  llm_call_id: number | null;
  is_hint: number;
  prompt_tokens: number | null;
  cached_tokens: number | null;
  completion_tokens: number | null;
  cost: number | null;
}

interface AdminRiddleSummary {
  riddle_id: number;
  number: number | null;
  title: string;
  started_at: string;
  solved_at: string | null;
  questions: number;
  verdicts: Record<string, number>;
  cost: number;
  failed_calls: number;
}

interface AdminRiddle {
  riddle_id: number;
  number: number | null;
  title: string;
  author: string;
  url: string;
  question: string;
  solution: string;
  started_at: string;
  solved_at: string | null;
  acknowledged_at: string | null;
  questions: AdminQuestion[];
  cost: number;
  failed_calls: number;
}

interface HintPreview {
  error: string | null;
  question: string | null;
  justification: string | null;
  verdict: string | null;
  comment: string | null;
  judge_justification: string | null;
  attempts: number | null;
  rejected: { question: string; reason: string }[];
  hint_call_id: number;
  judge_call_id: number | null;
}

interface LlmMessage {
  role: string;
  content?: string | null;
  tool_calls?: {
    id?: string;
    function?: { name?: string; arguments?: string };
  }[];
  tool_call_id?: string;
}

interface AdminCall {
  id: number;
  user_id: number;
  riddle_id: number;
  created_at: string;
  model: string;
  prompt_tokens: number;
  cached_tokens: number;
  completion_tokens: number;
  cost: number | null;
  error: string | null;
  messages: LlmMessage[];
  question: {
    user_id: number;
    riddle_id: number;
    text: string;
    verdict: string;
  } | null;
}

const VERDICT_LABELS: Record<string, string> = {
  yes: "Да",
  no: "Нет",
  irrelevant: "Несущ.",
  incorrect: "Некорр.",
  solved: "Угадал",
};

// SQLite CURRENT_TIMESTAMP is UTC without a zone marker.
function parseTs(ts: string): Date {
  return new Date(`${ts.replace(" ", "T")}Z`);
}

function fmtDateTime(ts: string | null): string {
  if (!ts) return "—";
  return parseTs(ts).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtTime(ts: string): string {
  return parseTs(ts).toLocaleTimeString("ru-RU");
}

function fmtDuration(from: string, to: string): string {
  const minutes = Math.round(
    (parseTs(to).getTime() - parseTs(from).getTime()) / 60_000,
  );
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} ч ${minutes % 60} мин`;
  return `${Math.floor(hours / 24)} дн`;
}

// USD; sub-cent amounts are common, so keep more precision there.
function fmtCost(usd: number | null): string {
  if (usd === null) return "—";
  return `$${usd.toFixed(usd !== 0 && Math.abs(usd) < 1 ? 4 : 2)}`;
}

// "in/out", plus the share of input tokens served from the prompt cache.
function fmtTokens(
  prompt: number | null,
  completion: number | null,
  cached: number | null = null,
): string {
  if (prompt === null && completion === null) return "";
  const text = `${(prompt ?? 0).toLocaleString("ru-RU")}/${(completion ?? 0).toLocaleString("ru-RU")}`;
  if (!prompt || cached === null) return text;
  return `${text} · кэш ${Math.round((100 * cached) / prompt)}%`;
}

function chip(kind: string, text: string): HTMLElement {
  return el("span", { class: `chip chip-${kind}` }, text);
}

function verdictChip(verdict: string): HTMLElement {
  return chip(verdict, VERDICT_LABELS[verdict] ?? verdict);
}

function table(
  headers: [string, string?][],
  rows: Child[][],
  cls = "",
): HTMLElement {
  const colClass = headers.map(([, c]) => c ?? "");
  return el(
    "table",
    { class: `grid ${cls}` },
    el(
      "thead",
      {},
      el(
        "tr",
        {},
        ...headers.map(([h, c]) => el("th", c ? { class: c } : {}, h)),
      ),
    ),
    el(
      "tbody",
      {},
      ...rows.map((r) =>
        el(
          "tr",
          {},
          ...r.map((c, i) =>
            el("td", colClass[i] ? { class: colClass[i] } : {}, c),
          ),
        ),
      ),
    ),
  );
}

function adminNav(...crumbs: Child[]): void {
  const parts: Child[] = [pageLink("admin", "Админка")];
  for (const c of crumbs) parts.push(" › ", c);
  header.replaceChildren(
    el(
      "nav",
      { class: "crumbs" },
      ...parts,
      el(
        "a",
        { class: "to-game", href: pageHref("") },
        "← к игре",
      ),
    ),
  );
}

// The player's #id, followed by a link to their account in the SSO admin.
function playerLabel(userId: string | number, text: Child): HTMLElement {
  return el(
    "span",
    { class: "nowrap" },
    text,
    " ",
    el(
      "a",
      {
        href: `${config.loginUrl}/adminz/core/user/${userId}/`,
        target: "_blank",
        title: "db.crem.xyz",
      },
      "↗",
    ),
  );
}

function stat(label: string, value: Child): HTMLElement {
  return el(
    "div",
    { class: "stat" },
    el("div", { class: "stat-value" }, value),
    el("div", { class: "stat-label" }, label),
  );
}

async function renderAdminUsers(): Promise<void> {
  adminNav();
  const { users, months } = await api<AdminOverview>("GET", "/api/admin/users");
  const sum = (key: "questions" | "solved" | "cost") =>
    users.reduce((n, u) => n + u[key], 0);
  // Failed calls made before user tracking have no user, so take the
  // all-time cost from the monthly totals rather than summing users.
  const totalCost = months.reduce((n, m) => n + m.cost, 0);
  transcript.replaceChildren(
    el(
      "div",
      { class: "stats" },
      stat("игроков", String(users.length)),
      stat("решено", String(sum("solved"))),
      stat("вопросов", String(sum("questions"))),
      stat("стоимость, всего", fmtCost(totalCost)),
      stat("стоимость, этот месяц", fmtCost(months[0]?.cost ?? 0)),
    ),
    el("h3", {}, "Игроки"),
    table(
      [
        ["Игрок", "num"],
        ["Решено", "num"],
        ["Вопросов", "num"],
        ["Вопр./данетку", "num"],
        ["Ошибок LLM", "num"],
        ["Токены in/out", "num"],
        ["Стоимость", "num"],
        ["$/данетку", "num"],
        ["Текущая данетка"],
        ["Активность", "nowrap"],
      ],
      users.map((u) => [
        playerLabel(u.user_id, pageLink(`admin=${u.user_id}`, `#${u.user_id}`)),
        `${u.solved} / ${u.started}`,
        String(u.questions),
        u.started ? (u.questions / u.started).toFixed(1) : "—",
        String(u.failed_calls),
        fmtTokens(u.prompt_tokens, u.completion_tokens, u.cached_tokens),
        fmtCost(u.cost),
        u.started ? fmtCost(u.cost / u.started) : "—",
        u.current_title ?? "—",
        fmtDateTime(u.last_activity),
      ]),
    ),
    el("h3", {}, "По месяцам"),
    table(
      [
        ["Месяц", "nowrap"],
        ["Игроков", "num"],
        ["Вызовов LLM", "num"],
        ["Ошибок", "num"],
        ["Токены in/out", "num"],
        ["Стоимость", "num"],
      ],
      months.map((m) => [
        m.month,
        String(m.users),
        String(m.calls),
        String(m.failed_calls),
        fmtTokens(m.prompt_tokens, m.completion_tokens, m.cached_tokens),
        fmtCost(m.cost),
      ]),
    ),
  );
}

function riddleStatus(r: {
  started_at: string;
  solved_at: string | null;
}): HTMLElement {
  return r.solved_at
    ? chip("solved", `решена за ${fmtDuration(r.started_at, r.solved_at)}`)
    : chip("open", "в процессе");
}

function verdictCounts(counts: Record<string, number>): HTMLElement {
  return el(
    "span",
    { class: "verdict-counts" },
    ...Object.entries(counts).flatMap(([v, n]) => [verdictChip(v), `${n} `]),
  );
}

function riddleLink(userId: string | number, riddleId: number, text: string) {
  return pageLink(`admin=${userId}&riddle=${riddleId}`, text);
}

function renderAdminRiddle(r: AdminRiddle): HTMLElement {
  const counts: Record<string, number> = {};
  for (const q of r.questions) counts[q.verdict] = (counts[q.verdict] ?? 0) + 1;
  return el(
    "section",
    { class: "riddle" },
    el(
      "div",
      { class: "riddle-head" },
      el("strong", {}, r.title),
      " ",
      riddleStatus(r),
      el(
        "span",
        { class: "muted" },
        ` №${r.number ?? "?"} · id ${r.riddle_id} · ${r.author || "автор неизвестен"} · `,
        r.url
          ? el(
              "a",
              { href: r.url, target: "_blank", rel: "noopener" },
              "danetka.ru",
            )
          : "",
        ` · начата ${fmtDateTime(r.started_at)} · ${r.questions.length} вопр. · ${fmtCost(r.cost)}`,
        r.failed_calls > 0 && ` · ошибок LLM: ${r.failed_calls}`,
      ),
      verdictCounts(counts),
    ),
    el(
      "div",
      { class: "riddle-text" },
      el("div", {}, el("div", { class: "label" }, "Условие"), r.question),
      el("div", {}, el("div", { class: "label" }, "Разгадка"), r.solution),
    ),
    r.questions.length
      ? table(
          [
            ["#", "num"],
            ["Время", "nowrap"],
            ["Вопрос", "w-q"],
            ["Ответ"],
            ["Комментарий", "w-c"],
            ["Обоснование", "w-j"],
            ["LLM", "num nowrap"],
          ],
          r.questions.map((q, i) => [
            String(i + 1),
            fmtTime(q.created_at),
            el(
              "span",
              {},
              q.is_hint ? chip("hint", "подсказка") : "",
              q.is_hint ? " " : "",
              q.text,
            ),
            verdictChip(q.verdict),
            q.comment,
            el("span", { class: "muted" }, q.justification),
            q.llm_call_id !== null &&
              el(
                "span",
                {},
                pageLink(`call=${q.llm_call_id}`, `#${q.llm_call_id}`),
                el(
                  "div",
                  { class: "muted" },
                  fmtTokens(
                    q.prompt_tokens,
                    q.completion_tokens,
                    q.cached_tokens,
                  ),
                ),
                el("div", { class: "muted" }, fmtCost(q.cost)),
              ),
          ]),
        )
      : el("p", { class: "muted" }, "Вопросов пока нет."),
  );
}

async function renderAdminUser(userId: string): Promise<void> {
  adminNav(playerLabel(userId, `Игрок #${userId}`));
  const riddles = await api<AdminRiddleSummary[]>(
    "GET",
    `/api/admin/user/${userId}`,
  );
  const solved = riddles.filter((r) => r.solved_at).length;
  const questions = riddles.reduce((n, r) => n + r.questions, 0);
  const cost = riddles.reduce((n, r) => n + r.cost, 0);
  transcript.replaceChildren(
    el(
      "div",
      { class: "stats" },
      stat("решено", `${solved} / ${riddles.length}`),
      stat("вопросов", String(questions)),
      stat(
        "вопр./данетку",
        riddles.length ? (questions / riddles.length).toFixed(1) : "—",
      ),
      stat("стоимость", fmtCost(cost)),
      stat("$/данетку", riddles.length ? fmtCost(cost / riddles.length) : "—"),
    ),
    table(
      [
        ["Начата", "nowrap"],
        ["№", "num"],
        ["Данетка"],
        ["Статус", "nowrap"],
        ["Вопросов", "num"],
        ["Ответы", "nowrap"],
        ["Ошибок LLM", "num"],
        ["Стоимость", "num"],
      ],
      riddles.map((r) => [
        fmtDateTime(r.started_at),
        String(r.number ?? "?"),
        riddleLink(userId, r.riddle_id, r.title),
        riddleStatus(r),
        String(r.questions),
        verdictCounts(r.verdicts),
        r.failed_calls ? String(r.failed_calls) : "",
        fmtCost(r.cost),
      ]),
    ),
  );
}

async function renderAdminUserRiddle(
  userId: string,
  riddleId: string,
): Promise<void> {
  const [riddle, all] = await Promise.all([
    api<AdminRiddle>("GET", `/api/admin/user/${userId}/riddle/${riddleId}`),
    api<AdminRiddleSummary[]>("GET", `/api/admin/user/${userId}`),
  ]);
  adminNav(pageLink(`admin=${userId}`, `Игрок #${userId}`), riddle.title);
  // The list is newest first, so "previous" is the one after it.
  const i = all.findIndex((r) => r.riddle_id === riddle.riddle_id);
  const step = (r: AdminRiddleSummary | undefined, arrow: string) =>
    r
      ? riddleLink(userId, r.riddle_id, arrow)
      : el("span", { class: "muted" }, arrow);
  transcript.replaceChildren(
    el(
      "nav",
      { class: "pager" },
      step(all[i + 1], "← предыдущая"),
      el("span", { class: "muted" }, `${all.length - i} из ${all.length}`),
      step(all[i - 1], "следующая →"),
    ),
    renderAdminRiddle(riddle),
    hintPreview(userId, riddleId),
  );
}

function callLink(id: number | null, text: string): Child {
  return id !== null && pageLink(`call=${id}`, text);
}

function renderHintPreview(h: HintPreview): HTMLElement {
  const calls = el(
    "div",
    { class: "muted" },
    "LLM: ",
    callLink(h.hint_call_id, `генератор #${h.hint_call_id}`),
    h.judge_call_id !== null && " · ",
    callLink(h.judge_call_id, `ведущий #${h.judge_call_id}`),
  );
  if (h.error !== null || h.question === null) {
    return el(
      "div",
      { class: "hint-preview" },
      chip("error", "ошибка"),
      " ",
      h.error,
      calls,
    );
  }
  return el(
    "div",
    { class: "hint-preview" },
    el(
      "div",
      {},
      el("strong", {}, h.question),
      " ",
      h.verdict && verdictChip(h.verdict),
      " ",
      h.comment,
      el("span", { class: "muted" }, ` · попыток: ${h.attempts}`),
    ),
    el(
      "div",
      {},
      el("span", { class: "label" }, "Генератор: "),
      el("span", { class: "muted" }, h.justification),
    ),
    el(
      "div",
      {},
      el("span", { class: "label" }, "Ведущий: "),
      el("span", { class: "muted" }, h.judge_justification),
    ),
    h.rejected.length > 0 &&
      el(
        "ul",
        { class: "muted" },
        ...h.rejected.map((r) =>
          el("li", {}, `«${r.question || "—"}» — ${r.reason}`),
        ),
      ),
    calls,
  );
}

// Dry run of /подсказка on the player's current state; nothing is stored.
function hintPreview(userId: string, riddleId: string): HTMLElement {
  const results = el("div", {});
  const button = el("button", { type: "button" }, "Сгенерировать подсказку");
  button.addEventListener("click", () => {
    button.setAttribute("disabled", "");
    const pending = el("div", { class: "hint-preview muted" }, "…");
    results.append(pending);
    api<HintPreview>(
      "POST",
      `/api/admin/user/${userId}/riddle/${riddleId}/hint`,
    )
      .then((h) => pending.replaceWith(renderHintPreview(h)))
      .catch((e: unknown) => {
        pending.replaceWith(
          el(
            "div",
            { class: "hint-preview" },
            chip("error", "ошибка"),
            " ",
            e instanceof Error ? e.message : String(e),
          ),
        );
      })
      .finally(() => button.removeAttribute("disabled"));
  });
  return el(
    "section",
    { class: "riddle" },
    el(
      "div",
      { class: "riddle-head" },
      button,
      el(
        "span",
        { class: "muted" },
        "Пробный прогон /подсказка: игроку ничего не сохраняется, стоимость учитывается.",
      ),
    ),
    results,
  );
}

function prettyJson(text: string | null | undefined): string {
  if (!text) return "";
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

function renderMessage(m: LlmMessage, i: number): HTMLElement {
  const body: Child[] = [];
  if (m.content) {
    const pre = el(
      "pre",
      {},
      m.role === "tool" ? prettyJson(m.content) : m.content,
    );
    body.push(
      m.role === "system"
        ? el(
            "details",
            {},
            el("summary", {}, `системный промпт (${m.content.length} симв.)`),
            pre,
          )
        : pre,
    );
  }
  for (const call of m.tool_calls ?? []) {
    body.push(
      el(
        "div",
        { class: "tool-call" },
        el("code", {}, `${call.function?.name ?? "?"}()`),
        el("pre", {}, prettyJson(call.function?.arguments)),
      ),
    );
  }
  return el(
    "div",
    { class: `msg msg-${m.role}` },
    el("div", { class: "msg-role" }, `${i + 1}. ${m.role}`),
    el("div", { class: "msg-body" }, ...body),
  );
}

async function renderAdminCall(callId: string): Promise<void> {
  const call = await api<AdminCall>("GET", `/api/admin/llm_call/${callId}`);
  const q = call.question;
  // Hint generator calls and failed calls have no question of their own.
  adminNav(
    pageLink(`admin=${call.user_id}`, `Игрок #${call.user_id}`),
    riddleLink(call.user_id, call.riddle_id, `Данетка ${call.riddle_id}`),
    `LLM #${call.id}`,
  );
  transcript.replaceChildren(
    el(
      "div",
      { class: "stats" },
      stat("модель", call.model),
      stat("время", fmtDateTime(call.created_at)),
      stat(
        "токены in/out",
        fmtTokens(
          call.prompt_tokens,
          call.completion_tokens,
          call.cached_tokens,
        ),
      ),
      stat("стоимость", fmtCost(call.cost)),
      stat(
        "ответ",
        q
          ? verdictChip(q.verdict)
          : call.error
            ? chip("error", "ошибка")
            : "—",
      ),
    ),
    el("p", {}, el("span", { class: "label" }, "Вопрос: "), q?.text ?? "—"),
    el("pre", { class: "error-box" }, call.error),
    ...call.messages.map(renderMessage),
  );
}

async function loadAdmin(): Promise<void> {
  document.body.classList.add("admin");
  document.getElementById("intro")!.hidden = true;
  form.hidden = true;
  const userId = params.get("admin");
  const riddleId = params.get("riddle");
  const callId = params.get("call");
  if (callId) await renderAdminCall(callId);
  else if (userId && riddleId) await renderAdminUserRiddle(userId, riddleId);
  else if (userId) await renderAdminUser(userId);
  else await renderAdminUsers();
}

async function main(): Promise<void> {
  header.replaceChildren(el("h1", {}, "Данетки"));
  transcript.replaceChildren(el("p", { class: "system" }, "Загрузка…"));
  try {
    await initAuth();
  } catch (e) {
    if (e instanceof NotLoggedIn) {
      showLogin();
    } else {
      handleError(e);
    }
    return;
  }
  const page =
    params.has("admin") || params.has("call")
      ? loadAdmin
      : params.has("solved")
        ? loadSolved
        : loadGame;
  await page().catch(handleError);
}

void main();

export {};
